import { Link, Navigate } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { can, useMe } from '../auth.tsx';
import { PageHeader, Panel } from '../components/ui.tsx';
import { WEB_MODULES } from '../modules/index.ts';
import { visibleAreas } from '../navigation.ts';
import { useUnreadCount } from '../notifications.tsx';
import { greetingKey, type HomeShortcut, homeShortcuts } from './home-shortcuts.ts';

type CountId = NonNullable<HomeShortcut['count']>;

/**
 * Início: saudação com a data, os números do dia (das mesmas consultas das telas de destino) e os
 * atalhos. Módulos novos acrescentam blocos aqui (comunicados, agenda…), nunca conteúdo de exemplo.
 */
export function HomePage() {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  const areas = useMemo(() => visibleAreas((p) => can(me, p)), [me]);
  const shortcuts = useMemo(() => homeShortcuts(areas), [areas]);
  const counts = useCounts(shortcuts);
  if (!me) return null;
  // Um módulo pode levar quem tem só as permissões dele a outra tela (ADR 0131).
  const permissions = me.permissions;
  const redirect = WEB_MODULES.map((m) => m.homeRedirect?.(permissions)).find((to) => !!to);
  if (redirect) return <Navigate to={redirect} />;
  const now = new Date();
  const today = new Intl.DateTimeFormat(i18n.language, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(now);
  const summary = shortcuts.filter((s) => s.count);
  const others = shortcuts.filter((s) => !s.count);
  return (
    <div className="mx-auto flex w-full max-w-[1180px] flex-col gap-6">
      <PageHeader
        title={t(greetingKey(now), { name: me.user.displayName })}
        description={today.charAt(0).toLocaleUpperCase(i18n.language) + today.slice(1)}
      />
      {/* Os números do dia: cada cartão abre a tela de onde o número vem. */}
      <ul aria-label={t('home.summary')} className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
        {summary.map((s) => {
          const Icon = s.icon;
          const count = counts[s.count as CountId];
          return (
            <li key={s.id}>
              <Link
                to={s.to}
                search={s.search}
                className="flex h-full items-start gap-4 rounded-lg border border-rule bg-sheet p-4 text-ink shadow-sm hover:border-control-border sm:items-center sm:px-5"
              >
                <span className="hidden size-10 shrink-0 items-center justify-center rounded-md bg-paper sm:flex">
                  <Icon size={20} strokeWidth={1.75} aria-hidden className="text-muted" />
                </span>
                <span className="flex min-w-0 grow flex-col">
                  {/* Enquanto carrega (ou se falhar), o cartão aparece sem número. */}
                  <span className="min-h-8 text-2xl font-semibold tabular-nums sm:min-h-9 sm:text-[28px]">
                    {count === undefined ? '' : count.toLocaleString(i18n.language)}
                  </span>
                  <span className="text-sm text-muted">{t(`home.summaryLabels.${s.id}`)}</span>
                </span>
                <ChevronRight
                  size={18}
                  strokeWidth={1.75}
                  aria-hidden
                  className="hidden shrink-0 text-control-border sm:block"
                />
              </Link>
            </li>
          );
        })}
      </ul>
      {others.length > 0 && (
        <div className="max-w-xl">
          <Panel title={t('home.shortcuts')}>
            <ul className="flex flex-col divide-y divide-rule">
              {others.map((s) => {
                const Icon = s.icon;
                return (
                  <li key={s.id}>
                    <Link
                      to={s.to}
                      className="flex min-h-12 items-center gap-2.5 py-2 text-ink hover:text-brand-text"
                    >
                      <Icon
                        size={18}
                        strokeWidth={1.75}
                        aria-hidden
                        className="shrink-0 text-muted"
                      />
                      <span className="flex min-w-0 grow flex-col">
                        <span className="text-sm font-medium">{t(s.titleKey)}</span>
                        {s.captionKey && (
                          <span className="text-xs text-muted">{t(s.captionKey)}</span>
                        )}
                      </span>
                      <ChevronRight
                        size={16}
                        strokeWidth={1.75}
                        aria-hidden
                        className="shrink-0 text-control-border"
                      />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </Panel>
        </div>
      )}
    </div>
  );
}

/**
 * Os números do dia, das mesmas consultas (e do mesmo cache) das telas de destino. Enquanto carrega
 * ou se falhar, o número fica em branco: nunca um valor inventado.
 */
function useCounts(_shortcuts: HomeShortcut[]): Record<CountId, number | undefined> {
  const unread = useUnreadCount();
  return { notifications: unread.data?.count };
}
